import React from 'react';
import { cn } from '@/lib/utils';
import {
  CheckCircle,
  Clock,
  AlertCircle,
  XCircle,
  Truck,
  Package,
  Ban,
  Sparkles,
  Mail,
  MailCheck,
  RefreshCw,
} from 'lucide-react';

/**
 * StatusBadge - Unified semantic status badge
 * 
 * @param {string} status - Status key (determines color + icon)
 * @param {string} label - Custom label (overrides default)
 * @param {boolean} showIcon - Show status icon
 * @param {string} size - 'sm' | 'md'
 * @param {string} className - Additional classes
 */

const STATUS_CONFIG = {
  // Success states
  success: {
    icon: CheckCircle,
    label: 'Concluído',
    classes: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  },
  received: {
    icon: CheckCircle,
    label: 'Recebida',
    classes: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  },
  paid: {
    icon: CheckCircle,
    label: 'Pago',
    classes: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  },
  closed: {
    icon: CheckCircle,
    label: 'Fechado',
    classes: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  },

  // Warning/Pending states
  pending: {
    icon: Clock,
    label: 'Pendente',
    classes: 'bg-amber-50 text-amber-700 border-amber-200',
  },
  draft: {
    icon: Clock,
    label: 'Rascunho',
    classes: 'bg-amber-50 text-amber-700 border-amber-200',
  },
  waiting: {
    icon: Clock,
    label: 'A aguardar',
    classes: 'bg-amber-50 text-amber-700 border-amber-200',
  },

  // Info/Processing states
  processing: {
    icon: RefreshCw,
    label: 'A processar',
    classes: 'bg-blue-50 text-blue-700 border-blue-200',
  },
  sent: {
    icon: Mail,
    label: 'Enviado',
    classes: 'bg-blue-50 text-blue-700 border-blue-200',
  },
  ordered: {
    icon: Truck,
    label: 'Encomendada',
    classes: 'bg-purple-50 text-purple-700 border-purple-200',
  },
  partial: {
    icon: Package,
    label: 'Parcial',
    classes: 'bg-blue-50 text-blue-700 border-blue-200',
  },
  ready: {
    icon: CheckCircle,
    label: 'Pronta',
    classes: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  },

  // Danger states
  danger: {
    icon: AlertCircle,
    label: 'Alerta',
    classes: 'bg-red-50 text-red-700 border-red-200',
  },
  error: {
    icon: XCircle,
    label: 'Erro',
    classes: 'bg-red-50 text-red-700 border-red-200',
  },
  failed: {
    icon: XCircle,
    label: 'Falhou',
    classes: 'bg-red-50 text-red-700 border-red-200',
  },
  cancelled: {
    icon: Ban,
    label: 'Anulado',
    classes: 'bg-slate-100 text-slate-500 border-slate-200 line-through',
  },
  out_of_stock: {
    icon: AlertCircle,
    label: 'Sem Stock',
    classes: 'bg-red-50 text-red-700 border-red-200',
  },
  low_stock: {
    icon: AlertCircle,
    label: 'Stock Baixo',
    classes: 'bg-orange-50 text-orange-700 border-orange-200',
  },

  // Communication states
  new_reply: {
    icon: MailCheck,
    label: 'Nova resposta',
    classes: 'bg-amber-50 text-amber-700 border-amber-300 animate-pulse',
  },
  reply_read: {
    icon: MailCheck,
    label: 'Resposta',
    classes: 'bg-emerald-50 text-emerald-600 border-emerald-200',
  },

  // AI states
  ai_suggestion: {
    icon: Sparkles,
    label: 'Sugestão AI',
    classes: 'bg-violet-50 text-violet-700 border-violet-200',
  },

  // Neutral
  neutral: {
    icon: null,
    label: '—',
    classes: 'bg-slate-100 text-slate-500 border-slate-200',
  },
};

export function StatusBadge({
  status,
  label: customLabel,
  showIcon = true,
  size = 'md',
  className,
  ...props
}) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.neutral;
  const Icon = config.icon;
  const displayLabel = customLabel || config.label;

  const sizeClasses = {
    sm: 'text-[10px] px-1.5 py-0.5 gap-1',
    md: 'text-xs px-2 py-0.5 gap-1.5',
  };

  const iconSizes = {
    sm: 'h-3 w-3',
    md: 'h-3.5 w-3.5',
  };

  return (
    <span
      className={cn(
        "inline-flex items-center font-medium rounded-md border whitespace-nowrap",
        config.classes,
        sizeClasses[size],
        className
      )}
      data-testid={`status-badge-${status}`}
      {...props}
    >
      {showIcon && Icon && (
        <Icon className={iconSizes[size]} />
      )}
      {displayLabel}
    </span>
  );
}

/**
 * Quick helper for stock status
 */
export function StockStatusBadge({ stock, reorderPoint, className }) {
  let status = 'success';
  let label = 'Em Stock';

  if (stock === 0) {
    status = 'out_of_stock';
    label = 'Sem Stock';
  } else if (reorderPoint !== null && stock <= reorderPoint) {
    status = 'low_stock';
    label = 'Stock Baixo';
  } else if (reorderPoint === null) {
    status = 'neutral';
    label = '—';
  }

  return <StatusBadge status={status} label={label} className={className} />;
}

/**
 * Quick helper for PO fulfillment status
 */
export function FulfillmentBadge({ fulfillment, className }) {
  const statusMap = {
    satisfeita: 'received',
    satisfeita_incompleta: 'received',
    parcial: 'partial',
    encomendada: 'ordered',
    pronta: 'ready',
    nao_satisfeita: 'warning',
    anulada: 'cancelled',
    pendente: 'draft',
  };

  const labelMap = {
    satisfeita: 'Recebida',
    satisfeita_incompleta: 'Recebida (parcial)',
    parcial: 'Parcial',
    encomendada: 'Encomendada',
    pronta: 'Pronta',
    nao_satisfeita: 'Não Satisfeita',
    anulada: 'Anulada',
    pendente: 'Rascunho',
  };

  return (
    <StatusBadge
      status={statusMap[fulfillment] || 'neutral'}
      label={labelMap[fulfillment] || fulfillment}
      className={className}
    />
  );
}

export default StatusBadge;
